// =============================================================================
// ActionButtons.tsx — Poker action buttons (Fold/Check/Call/Raise/All-in)
// Project: Texas Hold'em Social Casino
// =============================================================================
// Shows available actions to the player when it's their turn.
// Raise amount can be adjusted with a slider for desktop or +/- buttons on mobile.

import React, { useState, useCallback } from 'react'
import { useGameStore } from '../store/game-store'
import { ActionType, Player } from '../types'
import { useUIStore } from '../store/ui-store'

interface ActionButtonsProps {
  isMyTurn: boolean
  actions: ActionType[]
  currentBet: number
  myPlayer?: Player
  minRaise: number
}

export default function ActionButtons({ isMyTurn, actions, currentBet, myPlayer, minRaise }: ActionButtonsProps): React.ReactElement {
  const { sendAction } = useGameStore()
  const { isMobile } = useUIStore()
  const [raiseAmount, setRaiseAmount] = useState(minRaise || 20)
  const [showRaiseSlider, setShowRaiseSlider] = useState(false)

  const handleAction = useCallback((action: ActionType) => {
    if (!isMyTurn) return
    let amount = 0

    switch (action) {
      case 'call':
        amount = currentBet - (myPlayer?.currentBet ?? 0)
        break
      case 'raise':
        amount = raiseAmount
        break
      case 'all_in':
        amount = myPlayer?.chips ?? 0
        break
    }

    sendAction(action, amount)
    setShowRaiseSlider(false)
  }, [isMyTurn, currentBet, myPlayer, raiseAmount, sendAction])

  if (!isMyTurn || actions.length === 0) {
    return <div className="action-bar" style={{ minHeight: 48 }}>
      <span style={{ color: '#888', fontSize: 13 }}>{myPlayer?.name ? `${myPlayer.name}'s turn` : 'Waiting for players...'}</span>
    </div>
  }

  const hasFold = actions.includes('fold')
  const hasCheck = actions.includes('check')
  const hasCall = actions.includes('call')
  const hasRaise = actions.includes('raise')
  const hasAllIn = actions.includes('all_in')
  const maxRaise = myPlayer?.chips ?? 0

  const callAmount = currentBet - (myPlayer?.currentBet ?? 0)

  return (
    <div style={{ position: 'relative' }}>
      {/* Raise amount slider */}
      {showRaiseSlider && hasRaise && (
        <div style={{
          position: 'absolute', bottom: '100%', left: 0, right: 0,
          background: 'rgba(0,0,0,0.9)', padding: '12px 16px',
          borderTopLeftRadius: 12, borderTopRightRadius: 12,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontSize: 12, color: '#888' }}>Raise:</span>
            <span style={{ fontSize: 16, fontWeight: 700, color: '#f5c518' }}>{raiseAmount}</span>
          </div>
          <input
            type="range"
            min={minRaise || 20}
            max={maxRaise}
            value={raiseAmount}
            onChange={(e) => setRaiseAmount(Number(e.target.value))}
            style={{ width: '100%', accentColor: '#e8491d' }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#666' }}>
            <span>{minRaise}</span>
            <span>{maxRaise}</span>
          </div>
        </div>
      )}

      <div className="action-bar">
        {hasFold && (
          <button className="action-btn action-btn-fold" onClick={() => handleAction('fold')}>
            Fold
          </button>
        )}

        {hasCheck && (
          <button className="action-btn action-btn-check" onClick={() => handleAction('check')}>
            Check
          </button>
        )}

        {hasCall && (
          <button className="action-btn action-btn-call" onClick={() => handleAction('call')}>
            Call {callAmount > 0 && <span style={{ fontSize: 10, opacity: 0.8 }}>({callAmount})</span>}
          </button>
        )}

        {hasRaise && (
          <button
            className="action-btn action-btn-raise"
            onPointerDown={() => setShowRaiseSlider(v => !v)}
          >
            {showRaiseSlider ? (
              <span onClick={() => handleAction('raise')}>Raise {raiseAmount}</span>
            ) : 'Raise'}
          </button>
        )}

        {hasAllIn && (
          <button className="action-btn action-btn-allin" onClick={() => handleAction('all_in')}>
            All In
          </button>
        )}
      </div>
    </div>
  )
}
